import { useEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { Add } from '@mui/icons-material';
import { FILTER_FIRM_NUMBER } from 'common/constants/sidebar';
import { normalizePinnedItems, PINNED_FIRMS_KEY, PinnedItemId } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
} from 'components/ui';
import { OutlinedPushPin, PushPin } from 'icons';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from 'layouts/FirmBaseLayout/PrimaryNavigation/PrimaryNavigation.testIds';
import type { FirmSwitcherMenuProps } from './FirmSwitcherMenu.props';

const FirmSwitcherMenu = ({
  activeFirmSlug,
  anchorEl,
  canAddFirm,
  firms,
  onAddFirm,
  onClose,
  onFirmSelect,
  zoomLevel,
}: FirmSwitcherMenuProps) => {
  const [filterSearch, setFilterSearch] = useState('');
  const [hoveredFirmId, setHoveredFirmId] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [pinnedFirmsRaw, setPinnedFirms] = useLocalStorageByUser<PinnedItemId[]>(PINNED_FIRMS_KEY, []);
  const pinnedFirms = useMemo(() => normalizePinnedItems(pinnedFirmsRaw), [pinnedFirmsRaw]);

  const filteredFirms = useMemo(() => {
    let list = firms.map(firm => ({
      ...firm,
      isCurrent: firm.slug === activeFirmSlug,
      isPinned: pinnedFirms.some(pinnedFirm => pinnedFirm.value === firm.id),
    }));
    if (filterSearch) {
      list = list.filter(firm => firm.name.toLowerCase().includes(filterSearch));
    }
    return list.sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
  }, [activeFirmSlug, firms, filterSearch, pinnedFirms]);

  const showSearch = firms.length > FILTER_FIRM_NUMBER;

  const handleTogglePin = (event: SyntheticEvent, firm: (typeof filteredFirms)[number]) => {
    event.stopPropagation();
    if (typeof firm.id !== 'number') {
      return;
    }

    if (firm.isPinned) {
      setPinnedFirms(pinnedFirms.filter(pinnedFirm => pinnedFirm.value !== firm.id));
      return;
    }

    setPinnedFirms([...pinnedFirms, { value: firm.id }]);
  };

  // Reset search on close so each firm switch starts from the complete firm list.
  useEffect(() => {
    if (!anchorEl) {
      setFilterSearch('');
      setHoveredFirmId(null);
    }
  }, [anchorEl]);

  return (
    <PrimaryNavigationMenu
      id={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmSwitcherMenu}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmSwitcherMenu}
      menuMinWidth={200}
      zoomLevel={zoomLevel}
      scrollHintItemLabelPlural="firms"
      scrollHintItemLabelSingular="firm"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableAutoFocusItem
      TransitionProps={showSearch ? {
        onEntered: () => searchInputRef.current?.focus(),
      } : undefined}>

      {showSearch && (
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear firm search"
          inputRef={searchInputRef}
          fullWidth
          placeholder="Find a Firm"
          autoComplete="off"
          value={filterSearch}
          onClear={() => setFilterSearch('')}
          onChange={event => setFilterSearch(event.target.value.toLowerCase())}
        />
      )}

      {filteredFirms.length === 0 ? (
        <PrimaryNavigationMenuItem disabled>
          No firms found
        </PrimaryNavigationMenuItem>
      ) : (
        filteredFirms.map(firm => {
          const canSelectFirm = !firm.isCurrent;
          const currentFirmRowSx = firm.isCurrent ? { cursor: 'default' } : undefined;

          return (
            <PrimaryNavigationMenuItem
              key={firm.slug}
              aria-current={firm.isCurrent ? 'page' : undefined}
              isActive={firm.isCurrent}
              leadingSlot={
                <PrimaryNavigationMenuItemLeadingAction
                  aria-label={firm.isPinned ? 'Unpin firm' : 'Pin firm'}
                  isDisabled={typeof firm.id !== 'number'}
                  isVisible={firm.isPinned || hoveredFirmId === firm.id}
                  onClick={event => handleTogglePin(event, firm)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleTogglePin(event, firm);
                    }
                  }}>
                  {firm.isPinned ? <PushPin fontSize="inherit" /> : <OutlinedPushPin fontSize="inherit" />}
                </PrimaryNavigationMenuItemLeadingAction>
              }
              onClick={canSelectFirm ? () => onFirmSelect(firm) : undefined}
              onMouseEnter={() => setHoveredFirmId(typeof firm.id === 'number' ? firm.id : null)}
              onMouseLeave={() => setHoveredFirmId(null)}
              sx={currentFirmRowSx}
              data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmOption(firm.slug)}>
              {firm.name}
            </PrimaryNavigationMenuItem>
          );
        })
      )}

      {canAddFirm && (
        <PrimaryNavigationMenuAction
          icon={<Add />}
          onClick={onAddFirm}
          data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.addFirm}>
          Add Firm
        </PrimaryNavigationMenuAction>
      )}
    </PrimaryNavigationMenu>
  );
};

export default FirmSwitcherMenu;
